'use client';

import { useEffect, useState } from 'react';

import { useAutoComplete } from '@/shared/hooks';
import { CareerItem, LevelItem, RoleItem } from '@/shared/types';
import { ProfileInfoQueryResponse } from '@/shared/types/Profile/info.types';

interface ProfileInputProps {
  profileInfo?: ProfileInfoQueryResponse;
}

const useProfileInput = ({ profileInfo }: ProfileInputProps) => {
  const [role, setRole] = useState<string>('');
  const [career, setCareer] = useState<number>(0);
  const [level, setLevel] = useState<string>('');
  const [roleErrorMessage, setRoleErrorMessage] = useState<string>('');
  const [careerErrorMessage, setCareerErrorMessage] = useState<string>('');
  const [levelErrorMessage, setLevelErrorMessage] = useState<string>('');

  const {
    selectedSkill,
    staticSkillItems,
    skillQuery,
    searchedSkillData,
    setSkillQuery,
    handleSkillDelete,
    handleSkillInput,
    skillErrorMessage,
    setSkillErrorMessage,
  } = useAutoComplete();

  const addedSkills = selectedSkill.filter(
    (selectedSkill) =>
      !profileInfo?.skills.some((skill) => skill.name === selectedSkill)
  );
  const removedSkills = profileInfo?.skills
    .filter(
      (skill) =>
        !selectedSkill.some((selectedSkill) => skill.name === selectedSkill)
    )
    .map((skill) => skill.id);

  const handleRoleChange = (item: RoleItem) => {
    setRole(String(item.key));
    setRoleErrorMessage('');
  };

  const autoSetLevel = (key: number): string => {
    if (key < 0) return '';
    if (key < 4) return 'junior';
    if (key < 9) return 'mid_level';
    if (key >= 9) return 'senior';
    return '';
  };

  const handleCareerChange = (item: CareerItem) => {
    const key = Number(item.key);

    setLevel(autoSetLevel(key));
    setCareer(key);
    setCareerErrorMessage('');
    setLevelErrorMessage('');
  };

  const handleLevelChange = (item: LevelItem) => {
    setLevel(String(item.key));
    setLevelErrorMessage('');
  };

  const keyValue = (
    key: string | number,
    mapping: Record<string | number, string>
  ) => {
    if (!key) return { key: '', value: '' };
    return { key, value: mapping[key] };
  };

  const validate = () => {
    const validation = {
      roleError: !role ? '학생과 취업준비생은 관심 직무를 선택해 주세요.' : '',
      careerError: career === 0 ? '총 경력을 선택해 주세요.' : '',
      levelError: !level ? '주니어(1~3년), 미드레벨(4~8년), 시니어(9년~)' : '',
      skillError: selectedSkill.length === 0 ? '보유기술을 선택해 주세요.' : '',
    };

    setRoleErrorMessage(validation.roleError);
    setCareerErrorMessage(validation.careerError);
    setLevelErrorMessage(validation.levelError);
    setSkillErrorMessage(validation.skillError);

    return !Object.values(validation).some(Boolean);
  };

  useEffect(() => {
    if (profileInfo) {
      setRole(profileInfo.role);
      setCareer(profileInfo.career);
      setLevel(profileInfo.level);
      profileInfo.skills.forEach((skill) => {
        handleSkillInput(skill.name);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo]);

  return {
    data: {
      role,
      career,
      level,
      skill: {
        selectedSkill,
        skillQuery,
        staticSkillItems,
        searchedSkillData,
        addedSkills,
        removedSkills,
      },
    },
    action: {
      role: handleRoleChange,
      career: handleCareerChange,
      level: handleLevelChange,
      skill: { handleSkillDelete, handleSkillInput, setSkillQuery },
    },
    error: {
      roleErrorMessage,
      careerErrorMessage,
      levelErrorMessage,
      skillErrorMessage,
    },
    autoSetLevel,
    keyValue,
    validate,
  };
};
export default useProfileInput;
